import {
  deserializeRecipeCalculationBaseDto,
  FormulaViewType,
  Oxide,
  OxideRoles,
  RecipeCalculationBaseDto,
} from '@/services/api/api-client';

/**
 * Client-side glaze calculation engine, backed by the same C# code the backend uses for
 * validation, compiled to WebAssembly (see webapi/src/Seger.Calculator.Wasm). Running it in the
 * browser removes the per-keystroke HTTP round-trip to the on-the-fly / reduce-to-100 endpoints.
 *
 * The WASM assets are served from a fixed `/calculator` path (see the .NET static-file setup),
 * which revalidates on each load so a new release's engine is always picked up.
 */

/** A single ingredient as consumed by the engine (material formula inlined). */
export interface WasmCalcIngredient {
  formula: { [key in keyof typeof Oxide]?: number };
  value: number;
  isAddition: boolean;
}

/** Oxide-to-role mapping consumed by the engine. */
export interface WasmOxideRoleAssignment {
  oxide: Oxide;
  role: OxideRoles;
}

/** Full "on the fly" calculation request. Mirrors the C# CalculateRequest. */
export interface WasmCalculateRequest {
  recipe: WasmCalcIngredient[];
  oxideRoles: WasmOxideRoleAssignment[];
  formulaViewType: FormulaViewType;
  includeAdditionsIntoCalculations: boolean;
}

/** Full batch calculation request. Mirrors the C# CalculateBatchRequest. */
export interface WasmCalculateBatchRequest {
  recipe: WasmCalcIngredient[];
  amount: number;
  includeAdditions: boolean;
}

export enum InverseSolverKind {
  NnlsHeavyRow = 'NnlsHeavyRow',
  QpEquality = 'QpEquality',
}

export type WasmOxideFormula = Partial<Record<Oxide, number>>;
export type WasmCategorizedFormula = Partial<Record<OxideRoles, WasmOxideFormula>>;

export interface WasmInverseMaterial {
  id: number;
  name: string;
  formula: WasmOxideFormula;
}

export interface WasmInverseCalculateRequest {
  target: WasmOxideFormula;
  /**
   * Combined-alkali target: one constraint on Na₂O + K₂O rather than one per oxide, which is how
   * ceramicists normally think about R₂O flux. When set, neither Na₂O nor K₂O may appear in
   * `target` — the engine rejects the pair with a `ConflictingKNaOTarget` error.
   */
  kNaO?: number | null;
  palette: WasmInverseMaterial[];
  oxideRoles: WasmOxideRoleAssignment[];
  solver: InverseSolverKind;
}

export type InverseDeviationStatus = 'WithinTolerance' | 'Marginal' | 'Poor' | 'NotTargeted';

/**
 * One target row's deviation. Names a single `oxide`, or — when `isKNaO`, with `oxide` null — the
 * combined alkali, whose `achieved` is the sum of the achieved Na₂O and K₂O.
 */
export interface WasmInverseDeviation {
  oxide: Oxide | null;
  isKNaO: boolean;
  target: number | null;
  achieved: number;
  tolerance: number | null;
  displayTolerance: number | null;
  standardizedError: number | null;
  status: InverseDeviationStatus;
  displayStatus: InverseDeviationStatus;
}

export type InverseWarningKind = 'MissingSource' | 'MaterialEliminated';

export interface WasmInverseWarning {
  kind: InverseWarningKind;
  oxide: Oxide | null;
  isKNaO: boolean;
  materialId: number | null;
  materialName: string | null;
}

export interface WasmInverseCandidate {
  amounts: Array<{ materialId: number; value: number }>;
  achievedFormulas: {
    segerFormula: WasmCategorizedFormula;
    categorizedPercentsMolar: WasmCategorizedFormula;
    percentsMolar: WasmOxideFormula;
    weightParts: WasmOxideFormula;
  };
  deviations: WasmInverseDeviation[];
  optimizableFitScore: number | null;
  completeFitScore: number;
  warnings: WasmInverseWarning[];
}

export type InverseErrorKind =
  | 'EmptyTarget'
  | 'EmptyPalette'
  | 'PaletteTooLarge'
  | 'InvalidTarget'
  | 'InvalidMaterial'
  | 'DuplicateMaterialId'
  | 'InvalidOxideRoles'
  | 'InvalidSolver'
  | 'NoPositiveFluxTarget'
  | 'InfeasibleFluxConstraint'
  | 'SolverFailed'
  | 'ZeroFluxAfterRounding'
  | 'ConflictingKNaOTarget';

export interface WasmInverseCalculateResult {
  candidates: WasmInverseCandidate[];
  error: {
    kind: InverseErrorKind;
    oxide: Oxide | null;
    materialId: number | null;
  } | null;
}

/**
 * Result of converting a material formula between weight and molar percentages.
 * Mirrors the C# FormulaConversionResult.
 */
export interface WasmFormulaConversionResult {
  /** The full formula in the target unit, pass-through rows included. */
  formula: WasmOxideFormula;
  /** Oxides echoed unchanged because they have no molecular mass to convert with (Loi). */
  passthroughOxides: Oxide[];
  /**
   * Sum of the formula in weight percent, excluding pass-through rows. Under 100 it is the
   * material's loss on ignition, over 100 its gain — the fact molar percentages cannot carry, and
   * so the fact a conversion to molar loses. The way back is a declared LOI row, not this number.
   */
  weightSum: number;
}

/** Batch calculation result. Mirrors the C# BatchResult. */
export interface WasmBatchResult {
  /** Batch value for each ingredient, aligned by index to the request recipe. */
  values: number[];
  /** Sum of all batch values, rounded the same way the HTTP endpoint rounds it. */
  overallSum: number;
}

/** One resolved candidate column of a suggested recipe. Mirrors the C# ResolvedSlot. */
export interface WasmResolvedSlot {
  archetype: string;
  materialId: number;
  materialName: string;
  /** Filled from the public library — the user would need to acquire this material. */
  fromLibrary: boolean;
  /** Legacy donor-equivalence flag; Phase 3 uses `isGroup` instead. */
  viaEquivalence: boolean;
  /** Set when the slot was added to source a targeted oxide the main palette left uncovered. */
  fillsOxideGap: Oxide | null;
  alternativeMaterialIds: number[];
  /** True when `archetype` is a donor group (e.g. `b2o3_donor`) and the concrete donor was chosen here. */
  isGroup: boolean;
  /** Conditional inclusion rate of this archetype in the target region; 0 for gap-fill/search slots. */
  rate: number;
  /** True for the reserved column of an archetype; false for an extra candidate realization. */
  isPrimary: boolean;
}

export type WasmSuggestionOrigin = 'RateSelection' | 'MaterialSearch';

/** One ranked recipe suggestion. Mirrors the C# PaletteSuggestion. */
export interface WasmPaletteSuggestion {
  origin: WasmSuggestionOrigin;
  /** The single recommended recipe (the "magic button" result); the rest are alternates. */
  isPrimary: boolean;
  /** Closest result found, with at least one oxide outside the normal acceptance threshold. */
  isBestEffort: boolean;
  /** The target neighborhood was too sparse to trust; palette came from the global prior. */
  isExtrapolated: boolean;
  slots: WasmResolvedSlot[];
  unresolvedArchetypes: string[];
  candidate: WasmInverseCandidate;
  calculation: RecipeCalculationBaseDto;
}

interface CalculatorInterop {
  Calculate(requestJson: string): string;
  CalculateInverse(requestJson: string): string;
  ReduceRecipeTo100(recipeJson: string): string;
  ReduceMainTo100ScalingAdditions(recipeJson: string): string;
  CalculateBatch(requestJson: string): string;
  ConvertFormulaToMolar(formulaJson: string): string;
  ConvertFormulaToWeight(requestJson: string): string;
}

interface DotnetHost {
  create(): Promise<{
    getAssemblyExports(assemblyName: string): Promise<any>;
    getConfig(): { mainAssemblyName: string };
  }>;
}

// Path where the published .NET wasm bundle is served (frontend/public/calculator in dev,
// wwwroot/calculator in production). BASE_URL keeps it correct under a non-root base path.
const dotnetLoaderUrl = () => `${import.meta.env.BASE_URL}calculator/_framework/dotnet.js`;

// The .NET loader lives in /public, which Vite refuses to pull into the module graph (it is
// served as-is). Hiding the import behind `new Function` keeps Vite's static analysis from
// resolving it, so the browser imports the static file at runtime instead.
const importAtRuntime = new Function('url', 'return import(url)') as (
  url: string,
) => Promise<{ dotnet: DotnetHost }>;

type DotnetRuntime = Awaited<ReturnType<DotnetHost['create']>>;

const BOOT_TIMEOUT_MS = 60_000;

export class CalculatorBootError extends Error {
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'CalculatorBootError';
    if (cause !== undefined) {
      (this as { cause?: unknown }).cause = cause;
    }
  }
}

let interopPromise: Promise<CalculatorInterop> | null = null;
// The imported .NET loader module. A failed import (e.g. a network blip fetching dotnet.js) is not
// cached, so it can be retried — nothing has booted yet at that point.
let hostPromise: Promise<{ dotnet: DotnetHost }> | null = null;
// The booted runtime. `dotnet.create()` loads the .NET runtime module, which can only be created
// ONCE per page — a second call throws "Runtime module already loaded". So once create() has been
// attempted this promise is kept for the life of the page and never cleared, even if a later step
// (or the first calculation) fails. Re-running create() is what silently bricked the calculator
// until a full reload; a retry must reuse this runtime, never boot a second one.
let runtimePromise: Promise<DotnetRuntime> | null = null;
// Set once the runtime boot has definitively failed. Because boot cannot be retried in-page, every
// later caller then fails fast with this error (rather than re-waiting the whole boot timeout on
// each keystroke), and it surfaces as a CalculatorBootError so the app can show its error page.
let bootError: CalculatorBootError | null = null;

function markBootFailed(error: unknown): CalculatorBootError {
  if (!bootError) {
    bootError =
      error instanceof CalculatorBootError
        ? error
        : new CalculatorBootError('The calculator failed to start.', error);
  }
  return bootError;
}

function importHost(): Promise<{ dotnet: DotnetHost }> {
  if (!hostPromise) {
    hostPromise = importAtRuntime(dotnetLoaderUrl()).catch((error) => {
      hostPromise = null;
      throw error;
    });
  }
  return hostPromise;
}

function bootRuntime(runtime: Promise<DotnetRuntime>): Promise<DotnetRuntime> {
  return new Promise<DotnetRuntime>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(
        new CalculatorBootError(`The calculator did not start within ${BOOT_TIMEOUT_MS / 1000}s.`),
      );
    }, BOOT_TIMEOUT_MS);
    runtime.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

async function loadCalculator(): Promise<CalculatorInterop> {
  let runtime: DotnetRuntime;
  try {
    const { dotnet } = await importHost();
    if (!runtimePromise) {
      runtimePromise = dotnet.create();
    }
    runtime = await bootRuntime(runtimePromise);
  } catch (error) {
    // Only a failure at or after create() is unrecoverable; an import failure can still be retried.
    if (runtimePromise) {
      throw markBootFailed(error);
    }
    throw error;
  }

  const exports = await runtime.getAssemblyExports(runtime.getConfig().mainAssemblyName);
  return exports.Seger.Calculator.Wasm.CalculatorInterop as CalculatorInterop;
}

/**
 * Lazily initializes the WASM engine (once) and returns the callable interop.
 */
export function getCalculator(): Promise<CalculatorInterop> {
  if (bootError) {
    return Promise.reject(bootError);
  }
  if (!interopPromise) {
    interopPromise = loadCalculator().catch((error) => {
      interopPromise = null;
      throw error;
    });
  }
  return interopPromise;
}

/**
 * Warms up the WASM engine ahead of the first calculation so the multi-MB .NET runtime is loaded
 * (and instantiated) before the user starts typing. Idempotent — reuses the same instance as
 * {@link getCalculator}. Fire-and-forget: a failed preload is swallowed here and retried lazily
 * on the first real calculation.
 */
export function preloadCalculator(): void {
  void getCalculator().catch(() => {
    // Ignore preload failures; getCalculator() clears its cache so the next call retries.
  });
}

/** Runs a full on-the-fly calculation and returns the same shape as the HTTP endpoint. */
export async function calculateOnTheFly(
  request: WasmCalculateRequest,
): Promise<RecipeCalculationBaseDto> {
  const calculator = await getCalculator();
  const resultJson = calculator.Calculate(JSON.stringify(request));
  return deserializeRecipeCalculationBaseDto(resultJson);
}

/** Finds recipes for a target formula using the explicitly selected material palette. */
export async function calculateInverse(
  request: WasmInverseCalculateRequest,
): Promise<WasmInverseCalculateResult> {
  const calculator = await getCalculator();
  const resultJson = calculator.CalculateInverse(JSON.stringify(request));
  return JSON.parse(resultJson) as WasmInverseCalculateResult;
}

/**
 * Reduces a recipe to 100. Returns the reduced values aligned by index to the input recipe.
 * Formulas are not needed for this operation, so callers may pass empty formulas.
 */
export async function reduceRecipeTo100(recipe: WasmCalcIngredient[]): Promise<number[]> {
  const calculator = await getCalculator();
  const resultJson = calculator.ReduceRecipeTo100(JSON.stringify(recipe));
  return JSON.parse(resultJson) as number[];
}

/**
 * Reduces a recipe's main ingredients to 100 and scales every passed ingredient — additions
 * included — by that same factor. Unlike {@link reduceRecipeTo100}, additions are scaled too, so
 * they stay proportional to the new main total. Returns values aligned by index to the input
 * recipe. Formulas are not needed for this operation, so callers may pass empty formulas.
 */
export async function reduceMainTo100ScalingAdditions(
  recipe: WasmCalcIngredient[],
): Promise<number[]> {
  const calculator = await getCalculator();
  const resultJson = calculator.ReduceMainTo100ScalingAdditions(JSON.stringify(recipe));
  return JSON.parse(resultJson) as number[];
}

/**
 * Calculates batch values for a recipe. Returns the scaled values aligned by index to the input
 * recipe plus their overall sum. Formulas are not needed for batch scaling, so callers may pass
 * empty formulas.
 */
export async function calculateBatch(request: WasmCalculateBatchRequest): Promise<WasmBatchResult> {
  const calculator = await getCalculator();
  const resultJson = calculator.CalculateBatch(JSON.stringify(request));
  return JSON.parse(resultJson) as WasmBatchResult;
}

/**
 * Converts a material formula from the weight percentages it is stored in to molar percentages,
 * normalised to 100.
 */
export async function convertFormulaToMolar(
  formula: WasmOxideFormula,
): Promise<WasmFormulaConversionResult> {
  const calculator = await getCalculator();
  const resultJson = calculator.ConvertFormulaToMolar(JSON.stringify(formula));
  return JSON.parse(resultJson) as WasmFormulaConversionResult;
}

/**
 * Converts a material formula from molar back to weight percentages. The oxides land on the hundred
 * weight units a formula is quoted against, less whatever an `Loi` row in it declares — nothing is
 * carried over from the weights the molar values were converted from, because molar values do not
 * carry a loss on ignition. Callers editing in molar have to say so; see the material editor.
 */
export async function convertFormulaToWeight(
  formula: WasmOxideFormula,
): Promise<WasmFormulaConversionResult> {
  const calculator = await getCalculator();
  const resultJson = calculator.ConvertFormulaToWeight(JSON.stringify(formula));
  return JSON.parse(resultJson) as WasmFormulaConversionResult;
}
